import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Checkbox, Typography, CircularProgress, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CustomListbox from '../../../components/CustomListbox';

const BusinessProfileSelect = ({ profiles = [], isLoading = false, selected = [], totalProfiles, onChange }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const selectedObjects = useMemo(() => {
    if (!Array.isArray(profiles)) return [];
    return profiles.filter((p) => selected.includes(p.profile_id));
  }, [profiles, selected]);

  const sortedProfiles = useMemo(() => {
    return [...(profiles || [])].sort((a, b) => (a.org_id || '').localeCompare(b.org_id || ''));
  }, [profiles]);

  return (
    <Box>
      <Autocomplete
        multiple
        fullWidth
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={sortedProfiles || []}
        getOptionDisabled={(sortedProfiles) => sortedProfiles.status !== 'COMPLETED'}
        loading={isLoading}
        value={selectedObjects}
        inputValue={inputValue}
        disableCloseOnSelect
        ListboxComponent={CustomListbox}
        ListboxProps={{
          totalProfiles,
          onSelectAll: () => onChange((profiles || []).map((p) => p.profile_id)),
          onClearAll: () => onChange([]),
          sx: {
            backgroundColor: '#1e1e1e',
            paddingTop: 0,
            maxHeight: 256,
            overflowY: 'auto',
            '& .MuiAutocomplete-option': {
              color: '#fff',
              padding: '10px 1px',
              '&[aria-selected="true"]': {
                backgroundColor: '#1e1e1e !important',
              },
              '&:hover': {
                backgroundColor: '#4f46e5 !important',
              },
              '&[aria-selected="true"]:hover': {
                backgroundColor: '#4f46e5 !important',
              },
            },
          },
        }}
        onInputChange={(event, value, reason) => {
          if (reason === 'input') {
            setInputValue(value);
          }
        }}
        getOptionLabel={(option) => `${option?.name || ''} | ${option?.email || ''} | ${option?.tin || ''}`}
        isOptionEqualToValue={(option, value) => option.profile_id === value.profile_id}
        onChange={(_, newValue) => {
          const ids = newValue.map((v) => v.profile_id);
          onChange(ids);
        }}
        renderTags={(value) => {
          if (!value.length || open) return null;
          return <span style={{ marginLeft: 8, color: '#d1d5db' }}>{value.length} Business Profile(s) selected</span>;
        }}
        renderOption={(props, option, { selected }) => {
          const isDisabled = option.status !== 'COMPLETED';

          return (
            <li {...props}>
              <Checkbox
                checked={selected}
                disabled={isDisabled}
                icon={<span style={{ width: 16, height: 16 }} />}
                checkedIcon={<CheckIcon sx={{ color: '#fff', fontSize: 18 }} />}
                sx={{
                  padding: 0,
                  marginRight: 1.5,
                  marginTop: '3px',
                  color: '#fff',
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box>
                  <Typography fontSize={12} fontWeight={600} color="#fff">
                    {option.name}
                  </Typography>
                  <Typography fontSize={12} color="#aaa">
                    TIN: {option.tin}
                  </Typography>
                </Box>

                {isDisabled && (
                  <Chip
                    label={option.status}
                    size="small"
                    sx={{
                      marginLeft: 'auto',
                      fontSize: '10px',
                      height: 20,
                      backgroundColor: '#121212',
                      color: '#727efb',
                    }}
                  />
                )}
              </Box>
            </li>
          );
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            borderRadius: 2,
            marginTop: 0.5,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedObjects.length === 0 || open ? 'Search Business Profile' : ''}
            InputProps={{
              ...params.InputProps,
              sx: {
                paddingLeft: '8px',
                color: '#d1d5db',
                '& input::placeholder': {
                  color: '#9ca3af',
                  opacity: 1,
                },
              },
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default BusinessProfileSelect;
